import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import "../styles/Login.css";
import { getCsrfToken } from "../services/authService";
import { FaEye, FaEyeSlash } from "react-icons/fa";

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const redirect =
    searchParams.get("redirect") ||
    sessionStorage.getItem("postLoginRedirect") ||
    "/";

  const { authenticated, refreshUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [error, setError] = useState("");

  const registerUrl =
    redirect !== "/"
      ? `/register?redirect=${encodeURIComponent(redirect)}`
      : "/register";

  const forgotPasswordUrl =
    redirect !== "/"
      ? `/forgot-password?redirect=${encodeURIComponent(redirect)}`
      : "/forgot-password";

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const csrf = await getCsrfToken();

      const body = new URLSearchParams();
      body.append("email", form.email);
      body.append("password", form.password);

      const response = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          [csrf.headerName]: csrf.token,
        },
        body,
      });

      if (!response.ok) {
        setError(t("login.error"));
        return;
      }

      await refreshUser();
      sessionStorage.removeItem("postLoginRedirect");
      navigate(redirect, { replace: true });
    } catch {
      setError(t("login.genericError"));
    }
  };

  useEffect(() => {
    if (authenticated) {
      navigate(redirect, { replace: true });
    }
  }, [authenticated, navigate, redirect]);

  return (
    <main className="login-page">
      <section className="login-card">
        <h1>{t("login.title")}</h1>

        <p className="login-subtitle">{t("login.subtitle")}</p>

        <form onSubmit={handleSubmit} className="login-form">
          <label>
            {t("login.email")}
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
          </label>

          <label>
            {t("login.password")}

            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={handleChange}
                autoComplete="current-password"
                required
              />

              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Mostrar contraseña"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </label>

          {error && <p className="login-error">{error}</p>}

          <button type="submit" className="login-button">
            {t("login.submit")}
          </button>
        </form>

        <div className="login-links">
          <Link to={registerUrl}>{t("login.createAccount")}</Link>
          <Link to={forgotPasswordUrl}>{t("login.forgotPassword")}</Link>
        </div>
      </section>
    </main>
  );
}